import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { countUnread } from '../lib/unread';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const CustomerAuthContext = createContext(null);

export const useCustomerAuth = () => useContext(CustomerAuthContext);

// FastAPI 422 returns an array of {msg,...}; normalize any detail to a string.
export const formatApiError = (detail) => {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e))).filter(Boolean).join(' ');
  }
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
};

export const getCustomerToken = () => localStorage.getItem('customerToken');

export const CustomerAuthProvider = ({ children }) => {
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);

  const refreshUnread = useCallback(async () => {
    const t = localStorage.getItem('customerToken');
    if (!t) { setUnreadCount(0); return; }
    try {
      const res = await fetch(`${API_URL}/api/my/inquiries`, { headers: { Authorization: `Bearer ${t}` } });
      if (res.ok) {
        const list = await res.json();
        setUnreadCount(countUnread('cust', list, 'customer'));
      }
    } catch { /* ignore */ }
  }, []);

  const fetchMe = useCallback(async () => {
    const t = localStorage.getItem('customerToken');
    if (!t) {
      setCustomer(null);
      setLoading(false);
      return;
    }
    try {
      const res = await fetch(`${API_URL}/api/auth/me`, { headers: { Authorization: `Bearer ${t}` } });
      if (res.ok) {
        setCustomer(await res.json());
      } else {
        localStorage.removeItem('customerToken');
        setCustomer(null);
      }
    } catch {
      setCustomer(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  // Poll unread count while logged in (for the header badge)
  useEffect(() => {
    if (!customer) { setUnreadCount(0); return undefined; }
    refreshUnread();
    const t = setInterval(refreshUnread, 15000);
    return () => clearInterval(t);
  }, [customer, refreshUnread]);

  const login = async (email, password) => {
    const res = await fetch(`${API_URL}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(formatApiError(data.detail));
    localStorage.setItem('customerToken', data.token);
    setCustomer(data.customer);
    return data.customer;
  };

  const register = async (payload) => {
    const res = await fetch(`${API_URL}/api/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(formatApiError(data.detail));
    localStorage.setItem('customerToken', data.token);
    setCustomer(data.customer);
    return data.customer;
  };

  const logout = () => {
    localStorage.removeItem('customerToken');
    setCustomer(null);
    setUnreadCount(0);
  };

  return (
    <CustomerAuthContext.Provider value={{ customer, loading, login, register, logout, refresh: fetchMe, unreadCount, refreshUnread }}>
      {children}
    </CustomerAuthContext.Provider>
  );
};
